import { useEffect, useRef } from 'react';
import pollService from '../services/pollService';
import {
  readPendingClaim,
  clearPendingClaim,
  clearManageToken,
} from '../utils/pollTokens';

/**
 * Finish moving an account-less poll into the account its creator just made.
 *
 * THE FLOW THIS COMPLETES. Someone makes a poll at /poll/new with no account,
 * presses "make it private", and is sent to sign up. Signing up ends where
 * signing up normally ends — the onboarding wizard, then /schedule — rather than
 * bouncing back to a poll page they have finished with. So the claim has to be
 * finished from inside the app, and this is where.
 *
 * The intent is parked in localStorage (not sessionStorage) because OAuth
 * signup leaves and returns through another origin, and on some browsers that
 * is a different tab.
 *
 * Runs at most ONCE per mount and clears the intent whatever the outcome except
 * a transient failure, so a poll that was already claimed, deleted, or whose
 * token no longer matches cannot make every authenticated page retry forever.
 * Silent by design: nothing here is worth interrupting a brand-new user's first
 * screen with, and the poll is reachable from their meeting types either way.
 */
export function usePendingPollClaim(isAuthenticated: boolean): void {
  const attempted = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || attempted.current) return;

    const pending = readPendingClaim();
    if (!pending) return;

    attempted.current = true;

    pollService
      .claimPoll(pending.publicId, pending.adminToken)
      .then(() => {
        clearPendingClaim();
        // The server rotated the manage token during the claim, so the copy in
        // this browser now identifies nobody.
        clearManageToken(pending.publicId);
      })
      .catch((error: any) => {
        // 404 = no such poll or the token stopped matching; 409 = somebody
        // already owns it. Neither will ever succeed, so drop the intent.
        // Anything else (offline, a 500) keeps it for the next page load.
        if (error?.status === 404 || error?.status === 409) clearPendingClaim();
      });
  }, [isAuthenticated]);
}

export default usePendingPollClaim;
